import { OneApi } from '../components/one-api/one-api';
import type { EObject, Reference } from '../models';
import { EClass } from '../models';
import type { Card } from '../models/Cards/Card';
import type { DisplayMethod } from '../models/Methods/DisplayMethod';
import { ExecutionContext } from '../models/Methods/ExecutionContext';
import { ClassInterfacesCache } from '../utils/cache/class.interfaces.cache';
import { ONE_UI_API_CLASS_ID, ONE_UI_API_CLASS_IDENTIFIER, WORKSPACE_PACKAGE_IDENTIFIER } from './constants';
import type { FetchResponse, RestService } from '@sydle/ui-api';
import { buildRequestParams } from '@sydle/ui-api';

export type GetHistoryCardsResult = { cards: Card[]; totalElements: number; totalPages: number };

export type GetHistoryMethodsResult = {
  methods: DisplayMethod[];
};

export type GetFilePostInformationsResult = {
  fieldInformations: {
    multiple: boolean;
    readOnly: boolean;
    shiftable: boolean;
    acceptedExtensions: any;
    maxSize: number;
  };
};
export type GetInterfacesResult = { interfaces: Array<string>; type: string };
export type GetDisplayMethodsArgs = { _classId: string; _id?: string; executionContext?: ExecutionContext };
export type GetDisplayMethodsResponse = { methods: DisplayMethod[] };

export class OneUiApi {
  private restService: RestService;

  constructor(restService: RestService) {
    this.restService = restService;
  }

  public getHistoryCards(
    objectReference: Reference,
    size: number,
    page: number,
  ): Promise<FetchResponse<GetHistoryCardsResult>> {
    const requestParams = buildRequestParams({
      _classId: ONE_UI_API_CLASS_ID,
      methodIdentifier: 'getHistoryCards',
      queryParams: {
        _classId: objectReference._classId,
        _id: objectReference._id,
        size,
        page,
      },
    });
    const getResult = this.restService.get(requestParams);
    return getResult;
  }

  public async getDisplayMethods(
    reference: { _classId: string; _id?: string },
    executionContext?: ExecutionContext,
  ): Promise<FetchResponse<GetDisplayMethodsResponse>> {
    const isObjectExecutionContext = !!reference._id;
    return await OneApi.objectAPI.executeCustom({
      packageIdentifier: WORKSPACE_PACKAGE_IDENTIFIER,
      classIdentifier: ONE_UI_API_CLASS_IDENTIFIER,
      methodIdentifier: 'getDisplayMethods',
      method: 'GET',
      body: {
        objectRef: {
          _id: isObjectExecutionContext ? reference._id : EClass.CLASS_ID,
          _classId: reference._classId,
        },
        executionContext:
          executionContext || (isObjectExecutionContext ? ExecutionContext.OBJECT : ExecutionContext.CLASS),
      },
    });
  }

  public getFilePostInformations(): Promise<FetchResponse<GetFilePostInformationsResult>> {
    const requestParams = buildRequestParams({
      _classId: ONE_UI_API_CLASS_ID,
      methodIdentifier: 'getFilePostInformations',
    });
    const getResult = this.restService.get(requestParams);
    return getResult;
  }

  private async fetchInterfaces(classId): Promise<FetchResponse<GetInterfacesResult>> {
    const interfaces = await OneApi.objectAPI.executeCustom({
      packageIdentifier: WORKSPACE_PACKAGE_IDENTIFIER,
      classIdentifier: ONE_UI_API_CLASS_IDENTIFIER,
      methodIdentifier: 'getClassInterfaces',
      method: 'GET',
      queryParams: {
        _classId: classId,
      },
    });

    return interfaces;
  }

  public async getClassInterfaces(classId: string, forceRefetch = false): Promise<FetchResponse<any>> {
    const cacheInterfaces = ClassInterfacesCache.getClassInterfaces(classId);

    if (!forceRefetch && cacheInterfaces) {
      return {
        data: cacheInterfaces,
      };
    }

    const interfaces = await this.fetchInterfaces(classId);
    if (!interfaces.error) {
      ClassInterfacesCache.setClassInterfaces(classId, interfaces.data.interfaces, interfaces.data.type);
    }

    return interfaces;
  }

  public async getHistoryMethods(classId: string, objectId: string): Promise<FetchResponse<GetHistoryMethodsResult>> {
    const methods = await OneApi.objectAPI.executeCustom({
      packageIdentifier: WORKSPACE_PACKAGE_IDENTIFIER,
      classIdentifier: ONE_UI_API_CLASS_IDENTIFIER,
      methodIdentifier: 'getHistoryMethods',
      method: 'POST',
      body: {
        classId,
        _id: objectId,
      },
    });

    return methods;
  }

  public async getScannerMethods(): Promise<FetchResponse<GetHistoryMethodsResult>> {
    const methods = await OneApi.objectAPI.executeCustom({
      packageIdentifier: WORKSPACE_PACKAGE_IDENTIFIER,
      classIdentifier: ONE_UI_API_CLASS_IDENTIFIER,
      methodIdentifier: 'getScannerMethods',
      method: 'POST',
    });

    return methods;
  }

  public async getMask(maskId: string): Promise<FetchResponse<EObject>> {
    return await OneApi.objectAPI.executeCustom({
      packageIdentifier: WORKSPACE_PACKAGE_IDENTIFIER,
      classIdentifier: ONE_UI_API_CLASS_IDENTIFIER,
      methodIdentifier: 'getMask',
      method: 'GET',
      body: {
        _id: maskId,
      },
    });
  }
}
